import AppAdminFormLabel from '@/components/atoms/AppAdminFormLabel';
import AppButton from '@/components/atoms/AppButton';
import { RA2NDatePicker } from '@/components/organisms';
import { useAdminTheme } from '@/shared/providers/admin-theme/AdminThemeContext';
import type { RezeptLimitFormData } from '@/types/admin-rezept-limit';
import { convertSeirekiWrap } from '@/utils/wareki';
import { useState, useCallback, useMemo } from 'react';

interface RezeptLimitFormProps {
  initialValues?: {
    rezept_yearmonth?: string;
    limit_date?: string;
  };
  onSubmit: (data: RezeptLimitFormData) => Promise<void>;
  isLoading?: boolean;
}

const RezeptLimitForm = ({ initialValues, onSubmit, isLoading = false }: RezeptLimitFormProps) => {
  const { colors } = useAdminTheme();

  // Store ISO format internally (YYYY-MM for month, YYYY-MM-DD for date)
  const [yearmonthIso, setYearmonthIso] = useState(initialValues?.rezept_yearmonth ?? '');
  const [limitDateIso, setLimitDateIso] = useState(initialValues?.limit_date ?? '');
  const [errors, setErrors] = useState<{ rezept_yearmonth?: string; limit_date?: string }>({});

  const validate = useCallback((): boolean => {
    const newErrors: typeof errors = {};

    if (!yearmonthIso.trim()) {
      newErrors.rezept_yearmonth = '提出年月は必須です。';
    }

    if (!limitDateIso.trim()) {
      newErrors.limit_date = '提出期限は必須です。';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [yearmonthIso, limitDateIso]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    // yearmonthIso = "YYYY-MM" → YYYYMM integer
    const yyyymm = parseInt(yearmonthIso.replace(/-/g, '').slice(0, 6), 10);
    if (isNaN(yyyymm) || String(yyyymm).length !== 6) {
      setErrors(prev => ({
        ...prev,
        rezept_yearmonth: '提出年月は"YYYYMM"書式と一致していません。',
      }));
      return;
    }

    if (!limitDateIso || !/^\d{4}-\d{2}-\d{2}$/.test(limitDateIso)) {
      setErrors(prev => ({ ...prev, limit_date: '提出期限は正しい日付ではありません。' }));
      return;
    }

    try {
      await onSubmit({
        rezept_yearmonth: yyyymm,
        limit_date: limitDateIso,
      });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: Record<string, string[]> } } };
      const backendErrors = axiosErr?.response?.data?.errors;
      if (backendErrors) {
        setErrors({
          rezept_yearmonth: backendErrors.rezept_yearmonth?.[0],
          limit_date: backendErrors.limit_date?.[0],
        });
      }
    }
  }, [yearmonthIso, limitDateIso, validate, onSubmit]);

  // BD: 提出期限 datepicker opens at the month selected in 提出年月
  // Key forces remount when yearmonth changes → viewDate re-inits
  const limitDatePickerKey = useMemo(() => {
    return yearmonthIso || 'empty';
  }, [yearmonthIso]);

  // Pre-set 提出期限 datepicker to first day of selected month
  const limitDateValue = useMemo(() => {
    if (limitDateIso) return limitDateIso;
    if (!yearmonthIso) return '';
    return `${yearmonthIso}-01`;
  }, [yearmonthIso, limitDateIso]);

  const btnStyle = {
    backgroundColor: colors.primary,
    color: '#fff',
    borderColor: colors.primary,
  };

  return (
    <div className="max-w-md">
      <div className="mb-4">
        <AppAdminFormLabel label="提出年月" required className="mb-1" />
        <RA2NDatePicker
          picker="month"
          className="w-full"
          value={yearmonthIso}
          onChange={warekiStr => {
            // Convert wareki → "YYYY/MM" → "YYYY-MM"
            const seireki = convertSeirekiWrap(warekiStr).replace(/\//g, '-');
            setYearmonthIso(seireki);
            // Set limitDate to 1st of selected month
            setLimitDateIso(seireki ? `${seireki}-01` : '');
            if (errors.rezept_yearmonth)
              setErrors(prev => ({ ...prev, rezept_yearmonth: undefined }));
          }}
          placeholder="年月指定"
        />
        {errors.rezept_yearmonth && (
          <p className="text-red-500 text-sm mt-1">{errors.rezept_yearmonth}</p>
        )}
      </div>

      <div className="mb-4">
        <AppAdminFormLabel label="提出期限" required className="mb-1" />
        <RA2NDatePicker
          key={limitDatePickerKey}
          picker="day"
          className="w-full"
          value={limitDateValue}
          onChange={warekiStr => {
            const seireki = convertSeirekiWrap(warekiStr).replace(/\//g, '-');
            setLimitDateIso(seireki);
            if (errors.limit_date) setErrors(prev => ({ ...prev, limit_date: undefined }));
          }}
          placeholder="年月日指定"
        />
        {errors.limit_date && <p className="text-red-500 text-sm mt-1">{errors.limit_date}</p>}
      </div>

      <div className="flex justify-end">
        <AppButton
          onClick={handleSubmit}
          disabled={isLoading}
          style={btnStyle}
          className="font-medium"
        >
          {isLoading ? '保存中...' : '更新'}
        </AppButton>
      </div>
    </div>
  );
};

export default RezeptLimitForm;
